/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
import { useEffect, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { calculateBadgeData } from '@/utils/calculateBadgeData';
import { formatDateForMeasure } from '@/utils/formatDateForMeasure';
import { getTenantTimeRange } from '@/utils/getTenantTimeRange';
import { AppListItem } from 'types/app-list';
import {
  FilterType,
  MeasuresCount,
  MeasuresData,
} from 'types/measures-api-data';

import { System } from '@/components/icons';

import { Badge } from '../../ux-common/src/components/ui/badge';
import { EnhancedMultiSelect } from '../combobox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface AppFilterProps {
  data: AppListItem[];
  selectedApps: string[];
  setSelectedApps: (selectedValues: string[]) => void;
  showExamples: boolean;
  setShowExamples: (value: boolean) => void;
  appsForMeasures: string[];
  isFirstLoad: boolean;
  activeFilter: FilterType;
  setActiveFilter: (filter: FilterType) => void;
  measuresData: MeasuresData;
  loading: boolean;
}

export const AppFilter = ({
  data,
  selectedApps,
  setSelectedApps,
  showExamples,
  setShowExamples,
  activeFilter,
  setActiveFilter,
  measuresData, // Receive from parent
  loading, // Receive from parent
}: AppFilterProps) => {
  const [badgeData, setBadgeData] = useState<MeasuresCount>({
    errors: 0,
    not_meet_sla: 0,
    not_meet_budget_sla: 0,
    test_failures: 0,
  });

  const { messages } = useLanguage();
  const endTime = localStorage.getItem('measuresEndTime');

  useEffect(() => {
    if (Object.keys(measuresData).length > 0) {
      const calculatedBadgeData = calculateBadgeData(
        measuresData,
        data,
        showExamples
      );
      setBadgeData(calculatedBadgeData);
    } else {
      setBadgeData({
        errors: 0,
        not_meet_sla: 0,
        not_meet_budget_sla: 0,
        test_failures: 0,
      });
    }
  }, [measuresData, data, showExamples]); // Recalculate on any change

  const errorFilterHandler = () => {
    // If the user clicks the “Error” badge while already in the 'error' state, switch back to 'all'.
    setActiveFilter(activeFilter === 'error' ? 'all' : 'error');
  };
  const slaFilterHandler = () => {
    // Likewise for the performance/SLA badge.
    setActiveFilter(activeFilter === 'performance' ? 'all' : 'performance');
  };
  const budgetFilterHandler = () => {
    setActiveFilter(activeFilter === 'budget' ? 'all' : 'budget');
  };
  const testFailuresFilterHandler = () => {
    setActiveFilter(activeFilter === 'test_failures' ? 'all' : 'test_failures');
  };
  return (
    <div className="-mt-2 flex h-[52px] items-center rounded border border-line-tertiary px-3 py-2">
      {/* Filter by Application Type */}
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <p className="shrink-0 text-body leading-[19.6px] text-body-subtle">
              {messages?.DashboardPage?.category}:
            </p>
            <EnhancedMultiSelect
              options={Array.from(
                new Set(
                  data
                    .filter((app) => !app.is_playground)
                    .flatMap((app) => app.categories ?? [])
                )
              ).map((category, index) => ({
                id: index,
                label: category,
                value: category,
              }))}
              defaultValue={selectedApps}
              onChange={(e) => {
                const selectedValues = Array.from(
                  (e.target as HTMLSelectElement).selectedOptions,
                  (option) => option.value
                );
                setSelectedApps(selectedValues);
              }}
              onValueChange={setSelectedApps}
              placeholder={messages?.PromptFilters?.multi_select?.all}
              className="w-fit border-none p-0"
              variant="inverted"
            />
          </div>
          <div className="h-6 border border-line-tertiary"></div>

          {/* Filter error or performance. */}
          {loading ? (
            <p className="text-white">{messages?.Measures.fetching_stats}</p>
          ) : (
            <>
              <div className="mt-1 flex gap-2">
                {badgeData.errors > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger onClick={errorFilterHandler}>
                        <Badge
                          className={`h-6 cursor-pointer bg-transparent pl-2 pr-3 text-body font-normal leading-4 text-body-subtle hover:bg-transparent  ${activeFilter === 'error' ? 'bg-error-dark hover:bg-error-dark active:bg-error-dark' : null}`}
                        >
                          <span className="mr-[6px]">
                            {System.InfoIcon(
                              `h-[18px] w-[18px] ${activeFilter === 'error' ? 'text-white' : 'text-red-500 '}`
                            )}
                          </span>
                          <span
                            className={`${activeFilter === 'error' ? 'text-white' : null}`}
                          >
                            {messages?.General?.error} {badgeData?.errors}
                          </span>
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        align="center"
                        className="max-w-52 rounded"
                      >
                        {messages?.Measures.app_list_error}{' '}
                        {getTenantTimeRange() ||
                          messages?.PromptFilters?.date_range?.last7}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {badgeData.not_meet_sla > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger onClick={slaFilterHandler}>
                        <Badge
                          className={`h-6 cursor-pointer bg-transparent pl-2 pr-3 text-body font-normal leading-4 text-body-subtle hover:bg-transparent ${activeFilter === 'performance' ? 'bg-warning-dark hover:bg-warning-dark active:bg-warning-dark' : null}`}
                        >
                          <span className="text-brand-gray-06 mr-[6px]">
                            {System.BarIcon(
                              `h-[18px] w-[18px]  ${activeFilter === 'performance' ? 'text-black' : 'text-[#FC8A13]'}`
                            )}
                          </span>
                          <span
                            className={`${activeFilter === 'performance' ? 'text-black' : null}`}
                          >
                            {messages?.ApplicationForm?.performance}{' '}
                            {badgeData?.not_meet_sla}
                          </span>
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        align="center"
                        className="max-w-52 rounded"
                      >
                        {messages?.Measures.app_list_performance}{' '}
                        {getTenantTimeRange() ||
                          messages?.PromptFilters?.date_range?.last7}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {badgeData.not_meet_budget_sla > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger onClick={budgetFilterHandler}>
                        <Badge
                          className={`h-6 cursor-pointer bg-transparent pl-2 pr-3 text-body font-normal leading-4 text-body-subtle hover:bg-transparent ${activeFilter === 'budget' ? ' bg-[#FC8A1342] hover:bg-[#FC8A1342] active:bg-[#FC8A1342]' : null}`}
                        >
                          <span className="text-brand-gray-06 mr-[6px]">
                            {System.BudgetIcon(
                              `h-[18px] w-[18px] ${activeFilter === 'budget' ? 'text-[#FEA639]' : 'text-[#FC8A13]'}`
                            )}
                          </span>
                          <span
                            className={`${activeFilter === 'budget' ? 'text-white' : null}`}
                          >
                            {messages?.ApplicationForm?.budget}{' '}
                            {badgeData?.not_meet_budget_sla}
                          </span>
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        align="center"
                        className="max-w-52 rounded"
                      >
                        {messages?.Measures.app_list_budget}{' '}
                        {getTenantTimeRange() ||
                          messages?.PromptFilters?.date_range?.last7}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {badgeData.test_failures > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger onClick={testFailuresFilterHandler}>
                        <Badge
                          className={`h-6 cursor-pointer bg-transparent pl-2 pr-3 text-body font-normal leading-4 text-body-subtle hover:bg-transparent ${activeFilter === 'test_failures' ? 'bg-warning-dark hover:bg-warning-dark active:bg-warning-dark' : null}`}
                        >
                          <span className="text-brand-gray-06 mr-[6px]">
                            {System.TestFailuresIcon(
                              `h-[18px] w-[18px]  ${activeFilter === 'test_failures' ? 'text-black' : 'text-[#DFC113]'}`
                            )}
                          </span>
                          <span
                            className={`${activeFilter === 'test_failures' ? 'text-black' : null}`}
                          >
                            {messages?.ApplicationForm?.test_failures}{' '}
                            {badgeData?.test_failures}
                          </span>
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        align="center"
                        className="max-w-52 rounded"
                      >
                        {messages?.Measures.app_list_test_failures}{' '}
                        {getTenantTimeRange() ||
                          messages?.PromptFilters?.date_range?.last7}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {badgeData.errors > 0 &&
                badgeData.not_meet_sla > 0 &&
                badgeData.not_meet_budget_sla > 0 &&
                badgeData.test_failures > 0 && (
                  <p className="ml-2 text-body-sm text-form-input-disabled">
                    {messages?.ApplicationDetailPage?.last_refreshed_on}{' '}
                    {endTime && formatDateForMeasure(endTime)}
                  </p>
                )}
            </>
          )}
        </div>
        {/* Toggle for Demo Applications */}
        <label className="flex items-center space-x-2">
          <span className="text-form-input-text text-body-subtle">
            {messages?.DashboardPage?.title?.demo_applications}
          </span>
          <div
            className={`relative h-5 w-10 cursor-pointer rounded-[10px] transition duration-200 ease-in-out ${
              showExamples
                ? 'bg-form-control-border-focus'
                : 'border border-[#40565A] bg-transparent'
            } rounded-full`}
          >
            <input
              type="checkbox"
              checked={showExamples}
              onChange={(e) => setShowExamples(e.target.checked)}
              className="h-0 w-0 opacity-0"
            />
            <span
              className={`absolute inset-0 my-auto h-3 w-3 transform rounded-full transition duration-200 ease-in-out ${
                showExamples
                  ? 'translate-x-6 bg-body-primary'
                  : 'translate-x-1 bg-[#8C9EA1]'
              }`}
            ></span>
          </div>
        </label>
      </div>
    </div>
  );
};
